import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Tooltip, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AssignmentIcon from "@mui/icons-material/Assignment";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import SettingsIcon from "@mui/icons-material/Settings";

import GiverItems from "./GiverItems";
import GiverItemRequests from "./GiverItemRequests";
import NewItemRequests from "./NewItemRequests";
import GenerosityTestimonies from "./GenerosityTestimonies";
import ModuleSettingsModal from "../components/ModuleSettingsModal";

const TABS = [
  {
    value: "items",
    label: "Shared Items",
    description: "Items community members have offered to give away. Review each item and its admin approval status.",
    icon: CardGiftcardIcon,
  },
  {
    value: "item-requests",
    label: "Item Applications",
    description: "Applications from members who want to receive an item that someone has shared. Review and manage each application.",
    icon: SwapHorizIcon,
  },
  {
    value: "new-requests",
    label: "Support Requests",
    description: "Requests from members asking the community to provide or sponsor an item. Review and manage each need request.",
    icon: AssignmentIcon,
  },
  {
    value: "testimonies",
    label: "Testimonials",
    description: "Stories and feedback shared by members about their Share & Accept experience.",
    icon: VolunteerActivismIcon,
  },
];

const ShareAndAccept = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpDialog, setHelpDialog] = useState(null);

  const getTabFromLocation = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["items", "item-requests", "new-requests", "testimonies"].includes(tabParam)) {
      return tabParam;
    }
    const path = location.pathname;
    if (path.includes("giver-item-requests")) return "item-requests";
    if (path.includes("new-item-requests")) return "new-requests";
    if (path.includes("generosity-testimonies")) return "testimonies";
    return "items";
  };

  const [activeTab, setActiveTab] = useState(getTabFromLocation);

  useEffect(() => {
    setActiveTab(getTabFromLocation());
  }, [location.pathname, searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const getModuleConfig = () => {
    if (activeTab === "item-requests") {
      return { key: "giver_item_requests", title: "Giver Item Requests" };
    }
    if (activeTab === "new-requests") {
      return { key: "new_item_requests", title: "New Item Requests" };
    }
    if (activeTab === "testimonies") {
      return { key: "generosity_testimonies", title: "Generosity Testimonies" };
    }
    return { key: "giver_items", title: "Shared Items" };
  };

  const BORDER_COLOR = isLight ? "rgba(43, 130, 144, 0.25)" : "rgba(76, 161, 175, 0.45)";
  const PANEL_BG = isLight ? "#FFFFFF" : "#182232";

  return (
    <Box sx={{ width: "100%", pb: 4, pt: 1 }}>
      <Helmet><title>Share & Accept - GPN</title></Helmet>

      {/* ── Top Bar: Module Title + Tabs in one continuous row with Settings on Right ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 2,
          mb: "-1.5px",
          px: 1,
        }}
      >
        {/* LEFT: Module Title + Tabs grouped together */}
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          {/* Module Title — styled as a label, NOT a button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              pb: 1,
              px: 1,
              mr: 3.5,
              userSelect: "none",
              cursor: "default",
              borderBottom: isLight ? "2.5px solid rgba(43, 130, 144, 0.65)" : "2.5px solid rgba(76, 161, 175, 0.65)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: isLight ? "rgba(43, 130, 144, 0.12)" : "rgba(76, 161, 175, 0.18)",
                border: isLight ? "1.5px solid rgba(43, 130, 144, 0.4)" : "1.5px solid rgba(76, 161, 175, 0.5)",
                color: isLight ? "#2B8290" : "#64d2e2",
                flexShrink: 0,
              }}
            >
              <VolunteerActivismIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{
                color: isLight ? "#0F172A" : "#e0f2fe",
                letterSpacing: "0.6px",
                fontSize: "1.25rem",
                lineHeight: 1.15,
                textTransform: "uppercase",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Share & Accept
            </Typography>
          </Box>

          {/* Vertical Divider separating Title from Tab buttons */}
          <Box
            sx={{
              width: "1.5px",
              height: 24,
              bgcolor: isLight ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.15)",
              mr: 2.5,
              mb: 0.8,
            }}
          />

          {/* Tab Items */}
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.6 }}>
            {TABS.map(({ value, label, description, icon: Icon }) => {
              const isActive = activeTab === value;
              return (
                <Box
                  key={value}
                  onClick={() => handleTabChange(value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    px: 2.4,
                    py: 1.1,
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: "0.88rem",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? (isLight ? "#2B8290" : "#FFFFFF") : (isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)"),
                    transition: "color 0.2s ease",
                    ...(isActive
                      ? {
                          backgroundColor: PANEL_BG,
                          borderTop: `1.5px solid ${BORDER_COLOR}`,
                          borderLeft: `1.5px solid ${BORDER_COLOR}`,
                          borderRight: `1.5px solid ${BORDER_COLOR}`,
                          borderRadius: "8px 8px 0 0",
                          position: "relative",
                          zIndex: 10,
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: "-2px",
                            height: "4px",
                            backgroundColor: PANEL_BG,
                            zIndex: 12,
                          },
                        }
                      : {
                          backgroundColor: "transparent",
                          border: "none",
                          "&:hover": {
                            color: isLight ? "#2B8290" : "#FFFFFF",
                          },
                        }),
                  }}
                >
                  <Icon sx={{ fontSize: 17, color: isActive ? (isLight ? "#2B8290" : "#4CA1AF") : (isLight ? "#64748B" : "rgba(255, 255, 255, 0.55)") }} />
                  <span>{label}</span>
                  <Tooltip title={`About ${label}`}>
                    <IconButton
                      size="small"
                      aria-label={`About ${label}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setHelpDialog({ title: label, description });
                      }}
                      sx={{
                        ml: 0.1,
                        p: 0.15,
                        color: isActive ? (isLight ? "#2B8290" : "#64d2e2") : (isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)"),
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
                        ?
                      </Typography>
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* RIGHT: Settings Icon Button (matching Prayers module style) */}
        <Box sx={{ mb: "6px", mr: 0.5, zIndex: 10 }}>
          <Tooltip title={`${getModuleConfig().title} Settings`} placement="left">
            <IconButton
              onClick={() => setSettingsOpen(true)}
              sx={{
                color: isLight ? "#2B8290" : "#64d2e2",
                bgcolor: isLight ? "rgba(43, 130, 144, 0.12)" : "rgba(76, 161, 175, 0.15)",
                border: isLight ? "1px solid rgba(43, 130, 144, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)",
                borderRadius: "8px",
                p: 0.85,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: isLight ? "rgba(43, 130, 144, 0.22)" : "rgba(76, 161, 175, 0.3)",
                  borderColor: isLight ? "#2B8290" : "#4CA1AF",
                  transform: "rotate(30deg)",
                },
              }}
            >
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Main Content Box: Connected directly under the active tab ── */}
      <Box
        sx={{
          width: "100%",
          border: `1.5px solid ${BORDER_COLOR}`,
          borderRadius: "0 10px 10px 10px",
          backgroundColor: PANEL_BG,
          backdropFilter: "blur(20px)",
          boxShadow: isLight ? "0 4px 20px rgba(0, 0, 0, 0.05)" : "0 10px 30px rgba(0, 0, 0, 0.35)",
          p: { xs: 2, sm: 2.8 },
          position: "relative",
          zIndex: 1,
          minHeight: "75vh",
        }}
      >
        {activeTab === "items" && <GiverItems hideHeader={true} />}
        {activeTab === "item-requests" && <GiverItemRequests hideHeader={true} />}
        {activeTab === "new-requests" && <NewItemRequests hideHeader={true} />}
        {activeTab === "testimonies" && <GenerosityTestimonies hideHeader={true} />}
      </Box>

      <ModuleSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        moduleKey={getModuleConfig().key}
        moduleTitle={getModuleConfig().title}
      />

      <Dialog
        open={Boolean(helpDialog)}
        onClose={() => setHelpDialog(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="share-accept-tab-help-title"
      >
        <DialogTitle id="share-accept-tab-help-title">{helpDialog?.title}</DialogTitle>
        <DialogContent dividers>
          <Typography>{helpDialog?.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialog(null)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShareAndAccept;
